import { z } from "zod";

// Schema para endereços
const enderecoSchema = z.object({
  cep: z
    .string()
    .optional()
    .refine((val) => {
      // Se não fornecido ou vazio, é válido (opcional)
      if (!val || val.trim() === "") return true;
      const cepLimpo = val.replace(/\D/g, "");
      return cepLimpo.length === 8;
    }, "CEP deve ter 8 dígitos"),
  logradouro: z.string().optional(),
  numero: z.string().optional(),
  bairro: z.string().optional(),
  cidade: z.string().optional(),
  uf: z
    .string()
    .optional()
    .refine((val) => {
      // Se não fornecido ou vazio, é válido (opcional)
      if (!val || val.trim() === "") return true;
      if (val.length !== 2) return false;
      const ufsValidas = [
        "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
        "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
        "RS", "RO", "RR", "SC", "SP", "SE", "TO"
      ];
      return ufsValidas.includes(val.toUpperCase());
    }, "UF inválida"),
  complemento: z.string().optional(),
  principal: z.boolean().default(false),
});

// Schema para telefones
const telefoneSchema = z.object({
  telefone: z
    .string()
    .optional()
    .refine((val) => {
      // Se não fornecido ou vazio, é válido (opcional)
      if (!val || val.trim() === "") return true;
      const telefoneLimpo = val.replace(/\D/g, "");
      // Telefone deve ter 10 ou 11 dígitos (fixo ou celular)
      return telefoneLimpo.length >= 10 && telefoneLimpo.length <= 11;
    }, "Telefone inválido. Use o formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX"),
  tipo: z.enum(["CELULAR", "FIXO", "WHATSAPP", "OUTRO"]).default("CELULAR"),
  principal: z.boolean().default(false),
});

// Schema para emails
const emailSchema = z.object({
  email: z
    .string()
    .refine((val) => {
      // Se não fornecido ou vazio, é válido (opcional)
      if (!val || val.trim() === "") return true;
      // Se fornecido, deve ser um email válido
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(val);
    }, "Email inválido")
    .transform((val) => (val ? val.toLowerCase() : "")),
  principal: z.boolean().default(false),
});

// Schema para documentos
const documentoSchema = z.object({
  tipo_documento: z.enum(["RG", "CPF", "CARTAO_SUS", "COMPROVANTE_ENDERECO", "OUTRO"]),
  nome_arquivo: z.string().optional(),
  arquivo: z.any().optional(),
  caminho_arquivo: z.string().optional(),
  id: z.number().optional(),
}).refine((data) => {
  // Se tem arquivo novo OU caminho_arquivo (arquivo já salvo), nome_arquivo é obrigatório
  const temArquivo = data.arquivo || data.caminho_arquivo;
  if (temArquivo && (!data.nome_arquivo || data.nome_arquivo.trim() === "")) {
    return false;
  }
  return true;
}, {
  message: "Nome do arquivo é obrigatório quando um arquivo é selecionado",
  path: ["nome_arquivo"] // Indica que o erro é no campo nome_arquivo
});

// Schema para convênios
const convenioSchema = z.object({
  id_convenio: z.number(),
  matricula: z.string().max(30, "Matrícula deve ter no máximo 30 caracteres").optional(),
  valor_consulta: z.number().optional(),
  observacoes: z.string().optional(),
});

// Schema principal para criar paciente
export const criarPacienteSchema = z.object({
  // Dados da pessoa
  pessoa: z.object({
    nome: z.string().min(1, "Nome é obrigatório").max(150, "Nome deve ter no máximo 150 caracteres"),
    tipo: z.enum(["FISICA", "JURIDICA"]).default("FISICA"),
    categoria: z.enum(["PACIENTE", "MEDICO", "FUNCIONARIO", "ADMIN", "EMPRESA", "OUTRO"]).default("PACIENTE"),
    genero: z.enum(["MASCULINO", "FEMININO", "OUTRO"]).optional(),
    data_nascimento: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true; // Opcional, então vazio é válido
          
          let dataISO = val;
          
          // Se está no formato YYYY-MM-DD (já convertido), usa diretamente
          const dateRegexISO = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegexISO.test(val)) {
            // Se está no formato dd/MM/yyyy, converte para ISO
            const dataLimpa = val.replace(/\D/g, "");
            
            // Verifica se tem 8 dígitos (ddMMyyyy)
            if (dataLimpa.length !== 8) {
              return false;
            }
            
            const dia = dataLimpa.slice(0, 2);
            const mes = dataLimpa.slice(2, 4);
            const ano = dataLimpa.slice(4, 8);
            
            dataISO = `${ano}-${mes}-${dia}`;
          }
          
          // Valida a data no formato ISO
          const partes = dataISO.split("-");
          const ano = parseInt(partes[0]);
          const mes = parseInt(partes[1]);
          const dia = parseInt(partes[2]);
          
          // Verifica se o ano tem 4 dígitos válidos
          if (partes[0].length !== 4 || ano < 1 || ano > 9999) {
            return false;
          }
          
          // Verifica se o mês é válido
          if (mes < 1 || mes > 12) {
            return false;
          }
          
          // Verifica se o dia é válido
          if (dia < 1 || dia > 31) {
            return false;
          }
          
          // Cria a data de forma explícita para evitar problemas de timezone
          const date = new Date(ano, mes - 1, dia);
          
          // Verifica se é uma data válida (ex: 31/02 não é válido)
          if (
            date.getFullYear() !== ano ||
            date.getMonth() !== mes - 1 ||
            date.getDate() !== dia
          ) {
            return false;
          }
          
          return true;
        },
        {
          message: "Data de nascimento inválida. Use o formato dd/MM/yyyy",
        }
      ),
    imagem_perfil: z.string().optional(),
  }),

  // Dados pessoa física
  pessoa_fisica: z.object({
    cpf: z
      .string()
      .min(11, "CPF é obrigatório")
      .refine((val) => {
        if (val.length === 0) return false;
        // Remove caracteres não numéricos
        const cpfLimpo = val.replace(/\D/g, "");
        return cpfLimpo.length === 11;
      }, "CPF inválido"),
    rg: z.string().max(20, "RG deve ter no máximo 20 caracteres").optional(),
  }),

  // Dados de contato
  enderecos: z
    .array(enderecoSchema)
    .default([]),
  telefones: z
    .array(telefoneSchema)
    .default([]),
  emails: z
    .array(emailSchema)
    .default([]),

  // Dados do paciente
  cartao_sus: z.string().optional(),
  observacoes: z.string().optional(),
  acompanhante: z.string().optional(),
  fone_acompanhante: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true; // Opcional
        const telefoneLimpo = val.replace(/\D/g, "");
        return telefoneLimpo.length >= 10 && telefoneLimpo.length <= 11;
      },
      "Telefone do acompanhante inválido. Use o formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX"
    ),
  endereco_acompanhante: z.string().optional(),
  matricula_convenio: z.string().max(20, "Matrícula do convênio deve ter no máximo 20 caracteres").optional(),
  status: z.boolean().default(true),

  // Convênios
  convenios: z.array(convenioSchema).default([]),

  // Documentos
  documentos: z.array(documentoSchema).default([]),
});

// Schema para atualizar paciente
export const atualizarPacienteSchema = criarPacienteSchema.partial();

export type CriarPacienteFormData = z.infer<typeof criarPacienteSchema>;
export type AtualizarPacienteFormData = z.infer<typeof atualizarPacienteSchema>;

